import React, { useState } from 'react';
import { FiPhone, FiCopy, FiCheck, FiX } from 'react-icons/fi';
import PhoneInput from '../PhoneInput/PhoneInput';
import { requestShopContact } from '../../api/storeApi';
import styles from './styles.module.css';

/*
 * WHEN WHATSAPP DID NOT OPEN (2026-10-10) — the in-app browser's door, turned around.
 *
 * Inside Instagram's and Facebook's browser the `whatsapp://` link opens nothing: the page stays, and for a month
 * the only fallback was one line of text with the shop's number in it. Thirty days of DK: every recorded in-app
 * tap `opened: false`; on 10-03 eight shoppers tapped and the shop received nothing; on 10-08 a buyer tapped twice,
 * eight seconds apart, then filled the whole form and left at payment. These are the shoppers who WANT to talk.
 *
 * So, three doors that work from inside an app's browser, in the order a phone lets them work:
 *   · CALL — `tel:` is handed to the dialler by every in-app browser, PROVIDED it is given a number a dialler
 *     can read: until 2026-10-14 it carried the shop's own spelling ('03-5551234') and the owner's tap did
 *     nothing. It is the same digits wa.me gets now, as +<country code><subscriber> (ShopActionButtons);
 *   · COPY the number — the clipboard works there too, and WhatsApp is one app switch away;
 *   · LEAVE A NUMBER — one field; the shop is told (a lead, the owner's WhatsApp alert, a drafted reply) and
 *     messages the shopper. The number goes to the shop and nowhere else: never into analytics.
 *
 * ONE COMPONENT, because the product page and the checkout both have this door and had each their own copy of
 * the text line — a fix in one would have left the other a wall (a shared component has twins).
 *
 * AND IT CLOSES (2026-10-14). The owner, inside Instagram on his own phone: the sheet came up under the WhatsApp
 * button and nothing put it away — on the checkout it stood between the shopper and the form for the rest of the
 * visit. There is an × now, and closing it means closed: the dismissal is remembered for this shop for the rest
 * of the session, so the next tap that opens nothing does not put the wall back up. Session, not forever — a
 * shopper who comes back tomorrow and still cannot open WhatsApp is still offered the doors. Every storage touch
 * is wrapped: a private window and a web view with site data blocked both throw on read.
 */

/** Where this shop's dismissal is remembered — one shop's × does not silence another's sheet. */
export const dismissedKey = (salonSlug) => `stylon:contactOrderFallback:closed:${salonSlug || ''}`;

function wasDismissed(salonSlug) {
  try {
    return typeof window !== 'undefined' && window.sessionStorage.getItem(dismissedKey(salonSlug)) === '1';
  } catch (_) {
    return false;
  }
}

function rememberDismissed(salonSlug) {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(dismissedKey(salonSlug), '1');
  } catch (_) { /* the state below still closes it for this page view */ }
}

export default function ContactOrderFallback({
  t, salonSlug, shopName, number, callHref, countryCode,
  productId = null, variantId = null, productName = null, page = 'product', lines = null, defaultPhone = '',
  /* On the checkout the shopper's phone is already a field of the page: the number typed there IS the number, and
     this renders no box of its own — a second PhoneInput inside the checkout made the page re-render without end. */
  phoneFieldAbove = false,
  onNoted = () => {}, testId = 'contact-order-fallback',
}) {
  const [closed, setClosed] = useState(() => wasDismissed(salonSlug));
  const [copied, setCopied] = useState(false);
  const [ownPhone, setOwnPhone] = useState(defaultPhone || '');
  const phone = phoneFieldAbove ? (defaultPhone || '') : ownPhone;
  const [state, setState] = useState('idle'); // idle | sending | done | failed

  const copy = async () => {
    onNoted('copy');
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) { /* the number is on the screen either way */ }
  };

  const digits = String(phone || '').replace(/\D/g, '');
  const canSend = digits.length >= 8 && state !== 'sending' && state !== 'done';

  const close = () => {
    // Counted like the other doors: a shopper who closes this sheet is a fact about the sheet (2026-10-15).
    onNoted('close');
    setClosed(true);
    rememberDismissed(salonSlug);
  };

  const send = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!canSend) return;
    setState('sending');
    onNoted('callback');
    try {
      await requestShopContact(salonSlug, {
        phone: String(phone).trim(),
        productId: productId || undefined,
        variantId: variantId || undefined,
        productName: productName || undefined,
        page,
        lines: Array.isArray(lines) && lines.length
          ? lines.slice(0, 10).map((l) => ({ name: String(l.name || '').slice(0, 120), quantity: Math.max(1, Math.min(99, Number(l.quantity) || 1)) }))
          : undefined,
      });
      setState('done');
    } catch (_) {
      setState('failed');
    }
  };

  if (closed) return null;

  return (
    <div className={styles.box} role="status" data-testid={testId}>
      <button
        type="button"
        className={styles.close}
        onClick={close}
        data-testid={`${testId}-close`}
        aria-label={t('contactOrder.close', { defaultValue: 'Close this message' })}
      >
        <FiX aria-hidden />
      </button>
      <span className={styles.line}>
        {t('contactOrder.whatsappNoOpen', { defaultValue: "WhatsApp didn't open? Send your order to {{number}}", number })}
      </span>
      <div className={styles.actions}>
        {callHref && (
          <a className={styles.action} href={callHref} onClick={() => onNoted('phone')} data-testid={`${testId}-call`}>
            <FiPhone aria-hidden />
            {t('contactOrder.call', { defaultValue: 'Call' })}
          </a>
        )}
        <button type="button" className={styles.action} onClick={copy} data-testid={`${testId}-copy`} aria-live="polite">
          {copied ? <FiCheck aria-hidden /> : <FiCopy aria-hidden />}
          {copied ? t('contactOrder.copied', { defaultValue: 'Copied' }) : t('contactOrder.copyNumber', { defaultValue: 'Copy number' })}
        </button>
      </div>
      {/* NOT A <form>: the checkout is one already, and a form inside a form is invalid HTML — a browser drops the
          inner one and the Send would have submitted the whole checkout. A group, with Enter handled by the field. */}
      <div className={styles.callback} role="group" aria-labelledby={`${testId}-callback-label`} data-testid={`${testId}-callback`}>
        <span className={styles.callbackLabel} id={`${testId}-callback-label`}>
          {t('contactOrder.callbackTitle', { defaultValue: 'Or leave your number — {{shop}} will WhatsApp you', shop: shopName || '' })}
        </span>
        {state === 'done' ? (
          <span className={styles.done} data-testid={`${testId}-callback-done`}>
            <FiCheck aria-hidden />
            {t('contactOrder.callbackDone', { defaultValue: 'Done — {{shop}} will WhatsApp you shortly', shop: shopName || '' })}
          </span>
        ) : (
          <div className={styles.callbackRow}>
            {phoneFieldAbove ? (
              <span className={styles.callbackNumber} data-testid={`${testId}-callback-number`}>
                {digits.length >= 8
                  ? phone
                  : t('contactOrder.callbackTypeAbove', { defaultValue: 'Type your phone number above first' })}
              </span>
            ) : (
              <PhoneInput
                value={phone}
                onChange={setOwnPhone}
                defaultCountryCode={countryCode}
                salonCountryCode={countryCode}
                preferDefaultCountry
                disabled={state === 'sending'}
                enterKeyHint="done"
                onSubmit={send}
                testId={`${testId}-phone`}
              />
            )}
            <button type="button" className={styles.send} disabled={!canSend} onClick={send} data-testid={`${testId}-callback-send`}>
              {state === 'sending' ? t('contactOrder.callbackSending', { defaultValue: 'Sending…' }) : t('contactOrder.callbackSend', { defaultValue: 'Send' })}
            </button>
          </div>
        )}
        {state === 'failed' && (
          <span className={styles.failed} role="alert" data-testid={`${testId}-callback-failed`}>
            {t('contactOrder.callbackFailed', { defaultValue: "That didn't go through — call us, or try again in a minute" })}
          </span>
        )}
      </div>
    </div>
  );
}
